// Central data: products + all catalogue colors with approximate hex swatches
// Colors approximated from the official Mist Paints catalogue names.

export const COMPANY = {
    name: "Mist Paints",
    tagline: "Colour, crafted in Oman.",
    phone: "+968 24155368",
    phoneRaw: "96824155368",
    whatsapp: "96824155368", // used for wa.me links
    email: "info@mistpaints.com",
    website: "www.mistpaints.com",
    address: "Rusayl Industrial Estate (Madayn), Muscat, Sultanate of Oman",
    addressLines: [
        "Rusayl Industrial Estate (Madayn)",
        "Muscat, Sultanate of Oman",
    ],
    founded: "Made in Oman",
};

export const CERTIFICATIONS = [
    "Ammonia Free",
    "Odor Control",
    "APEO Free",
    "Phthalate Free",
    "Lead Free",
    "Mercury Free",
    "Formaldehyde Free",
];

export const PRODUCTS = [
    { id: "int-primer", name: "Interior Primer", arabic: "برايمر داخلي", category: "Interior", blurb: "Deep-anchoring primer that grips gypsum and cement to give topcoats an even, unshakeable start.", accent: "#214D8D" },
    { id: "int-stucco", name: "Interior Stucco", arabic: "معجون ستكو داخلي", category: "Interior", blurb: "Fine-grade filler that erases hairlines and settles into a mirror-flat interior surface.", accent: "#33672E" },
    { id: "int-plastic", name: "Interior Plastic Emulsion", arabic: "دهان داخلي – مطفي", category: "Interior", blurb: "Everyday matt emulsion — soft, breathable, and quietly durable for family walls.", accent: "#EEC12B" },
    { id: "int-premium-matt", name: "Interior Premium Matt Emulsion", arabic: "دهان داخلي فاخر – مطفي", category: "Interior", blurb: "Ultra-fine matt with a velvet touch — the finish photographs like a gallery wall.", accent: "#EF6424" },
    { id: "int-easy-care", name: "Interior Premium Easy Care Emulsion", arabic: "دهان داخلي فاخر سهل العناية", category: "Interior", blurb: "Stain-resistant, wipe-clean and colour-locked — engineered for busy homes and quiet lobbies.", accent: "#ED2938" },
    { id: "ext-primer", name: "Exterior Primer", arabic: "برايمر خارجي", category: "Exterior", blurb: "Alkali-resistant base built to seal masonry against Oman's coastal humidity and desert heat.", accent: "#214D8D" },
    { id: "ext-filler", name: "Exterior Filler", arabic: "معجون فيلر خارجي", category: "Exterior", blurb: "High-build filler that levels facades and holds firm through thermal cycles.", accent: "#33672E" },
    { id: "ext-matt", name: "Exterior Matt Emulsion", arabic: "دهان خارجي - مطفي", category: "Exterior", blurb: "Weather-guard matt finish — soft light, hard defence.", accent: "#EEC12B" },
    { id: "ext-premium", name: "Exterior Premium Emulsion", arabic: "دهان خارجي فاخر - مطفي", category: "Exterior", blurb: "Sun-, salt- and dust-resistant premium coating with long-lasting colour depth.", accent: "#EF6424" },
    { id: "ext-texture", name: "Exterior Medium Texture", arabic: "دهان خارجي متوسط الملمس", category: "Exterior", blurb: "Balanced tactile finish — smoothness with a whisper of grain, catching afternoon light.", accent: "#ED2938" },
    { id: "syn-undercoat", name: "Synthetic Undercoat White", arabic: "دهان سنثتك أولي", category: "Wood & Metal", blurb: "Sealing white ground for wood and metal — accepts gloss topcoats with perfect uniformity.", accent: "#0E0E10" },
    { id: "red-oxide", name: "Red Oxide Primer", arabic: "ريد اوكسايد برايمر", category: "Wood & Metal", blurb: "Anti-corrosive iron-oxide primer for steel structures exposed to the elements.", accent: "#ED2938" },
    { id: "syn-enamel", name: "Synthetic Enamel Gloss", arabic: "دهان سنثتك - زيتي", category: "Wood & Metal", blurb: "High-gloss enamel with mirror-clear finish and long open-time for a flawless brushmark-free coat.", accent: "#EEC12B" },
    { id: "syn-varnish", name: "Synthetic Varnish Clear", arabic: "ورنيش سنثتك - شفاف", category: "Wood & Metal", blurb: "Crystal-clear varnish that lifts wood grain and shields against wear, moisture and UV.", accent: "#EF6424" },
    { id: "thinners", name: "Thinners", arabic: "تنر", category: "Wood & Metal", blurb: "Precision-grade thinners engineered to match Mist Paints' oil-based coatings.", accent: "#214D8D" },
];

// Color collections — approximate hex values derived from the catalogue color names.
const withHex = (arr) => arr.map((c) => ({ ...c }));

export const COLLECTIONS = {
    "exterior-texture": {
        title: "Exterior Medium Texture",
        subtitle: "Balanced tactile finish. A whisper of grain, a mile of durability.",
        prefix: "MIST",
        colors: withHex([
            { code: "MIST 1056", name: "Grecian White", hex: "#EFE9DD" },
            { code: "MIST 1137", name: "Sandstone", hex: "#D9C7A7" },
            { code: "MIST 1223", name: "Light Grey", hex: "#BDBEB8" },
            { code: "MIST 1524", name: "Diamond White", hex: "#F4F1EA" },
            { code: "MIST 1796", name: "Cloud White", hex: "#EDEBE2" },
            { code: "MIST 1474", name: "Pearl White", hex: "#EAE5D8" },
            { code: "MIST 1080", name: "Atlantic Green", hex: "#5F8A6B" },
            { code: "MIST 1945", name: "Bamboe Green", hex: "#8FA671" },
            { code: "MIST 1306", name: "Cosmic Latte", hex: "#F0E8D6" },
            { code: "MIST 1629", name: "Bermuda Blue", hex: "#7AA7B2" },
            { code: "MIST 1311", name: "Shell Yellow", hex: "#F0DEA6" },
            { code: "MIST 1480", name: "Chamois", hex: "#E9D3A3" },
            { code: "MIST 1541", name: "Silver Quartz", hex: "#C6C4BE" },
            { code: "MIST 1926", name: "Sand Met", hex: "#D6C2A2" },
            { code: "MIST 1809", name: "Sunset Clay", hex: "#C97C55" },
            { code: "MIST 1078", name: "Chocolate", hex: "#6B4A34" },
            { code: "MIST 1625", name: "Almond Beige", hex: "#E3D0B4" },
            { code: "MIST 1408", name: "Baby Pink", hex: "#F4D6D2" },
            { code: "MIST 1104", name: "Naples Beige", hex: "#EAD8B5" },
            { code: "MIST 1875", name: "Vanneau Grey", hex: "#9FA0A0" },
            { code: "MIST 1723", name: "Vert Green", hex: "#4E7A48" },
            { code: "MIST 1900", name: "Sunshine Yellow", hex: "#F3C548" },
            { code: "MIST 1663", name: "Lilac", hex: "#C9B7D9" },
            { code: "MIST 1009", name: "White", hex: "#FBF8F1" },
        ]),
    },
    "exterior-premium": {
        title: "Exterior Premium Emulsion",
        subtitle: "Refined, distinguished, sun-locked. Elegance that survives the elements.",
        prefix: "MIST",
        colors: withHex([
            { code: "MIST 2166", name: "Grecian White", hex: "#F1EBDE" },
            { code: "MIST 2330", name: "Sandstone", hex: "#DAC6A4" },
            { code: "MIST 2941", name: "Light Grey", hex: "#BEBFB9" },
            { code: "MIST 2510", name: "Diamond White", hex: "#F5F2EB" },
            { code: "MIST 2806", name: "Cloud White", hex: "#EEEBE3" },
            { code: "MIST 2714", name: "Pearl White", hex: "#EBE6DA" },
            { code: "MIST 1852", name: "Atlantic Green", hex: "#5F8A6B" },
            { code: "MIST 2146", name: "Bamboe Green", hex: "#8FA671" },
            { code: "MIST 2406", name: "Cosmic Latte", hex: "#F1E9D7" },
            { code: "MIST 2581", name: "Bermuda Blue", hex: "#7BA8B3" },
            { code: "MIST 2001", name: "Shell Yellow", hex: "#F0DEA6" },
            { code: "MIST 2719", name: "Chamois", hex: "#E9D3A3" },
            { code: "MIST 2954", name: "Silver Quartz", hex: "#C6C4BE" },
            { code: "MIST 2035", name: "Sunset Clay", hex: "#CA7C55" },
            { code: "MIST 2876", name: "Chocolate", hex: "#6B4A34" },
            { code: "MIST 2333", name: "Almond Beige", hex: "#E3D0B4" },
            { code: "MIST 2975", name: "Baby Pink", hex: "#F5D7D3" },
            { code: "MIST 2432", name: "Naples Beige", hex: "#EAD8B5" },
            { code: "MIST 2619", name: "Vanneau Grey", hex: "#9FA0A0" },
            { code: "MIST 2709", name: "Vert Green", hex: "#4E7A48" },
            { code: "MIST 2044", name: "Sunshine Yellow", hex: "#F3C548" },
            { code: "MIST 2567", name: "Lilac", hex: "#C9B7D9" },
            { code: "MIST 2899", name: "White", hex: "#FBF8F1" },
        ]),
    },
    "interior-premium": {
        title: "Interior Premium Emulsion",
        subtitle: "Elevated interior colour. Smooth, flawless, quietly luxurious.",
        prefix: "MIST",
        colors: withHex([
            { code: "MIST 3023", name: "Pearl Cream", hex: "#EFE4CB" },
            { code: "MIST 3905", name: "Vintage Cream", hex: "#EEDFC0" },
            { code: "MIST 3752", name: "Stone Beige", hex: "#D8C6A5" },
            { code: "MIST 3670", name: "Latte Mist", hex: "#D7C2A6" },
            { code: "MIST 3288", name: "Moonlit Grey", hex: "#B6B5AF" },
            { code: "MIST 3964", name: "Shadow Mist", hex: "#8C8B85" },
            { code: "MIST 3570", name: "Honey Bronze", hex: "#B48A4F" },
            { code: "MIST 3419", name: "Steel Tide", hex: "#7C8790" },
            { code: "MIST 3604", name: "Breez Blue", hex: "#A8C1D1" },
            { code: "MIST 3880", name: "Crystal White", hex: "#F6F2E9" },
            { code: "MIST 3508", name: "Morning Breeze", hex: "#DDE4DA" },
            { code: "MIST 3147", name: "Cool Horizon", hex: "#B9C7CE" },
            { code: "MIST 3096", name: "Palm Oasis", hex: "#7EA279" },
            { code: "MIST 3313", name: "Golden Lime", hex: "#D6C56A" },
            { code: "MIST 3619", name: "Desert Dune", hex: "#D3B688" },
            { code: "MIST 3481", name: "Earthen Glow", hex: "#B67B4E" },
            { code: "MIST 3994", name: "Olive Bloom", hex: "#8E9057" },
            { code: "MIST 3754", name: "Silk Rose", hex: "#E7C5C0" },
            { code: "MIST 3098", name: "Oat White", hex: "#EDE3CE" },
            { code: "MIST 3242", name: "Blush Sand", hex: "#E9CCB5" },
            { code: "MIST 3311", name: "Violet Bloom", hex: "#B29BC0" },
            { code: "MIST 3961", name: "Desert Biscuit", hex: "#D7B98F" },
            { code: "MIST 3564", name: "Storm Cloud", hex: "#8F9297" },
            { code: "MIST 3704", name: "Clear White", hex: "#F7F3EA" },
        ]),
    },
    "easy-care": {
        title: "Easy Care Colours",
        subtitle: "Stain-shrugging, wipe-clean colour. Beauty that survives real life.",
        prefix: "MIST",
        colors: withHex([
            { code: "MIST 4137", name: "Pearl Cream", hex: "#EFE4CB" },
            { code: "MIST 4607", name: "Vintage Cream", hex: "#EEDFC0" },
            { code: "MIST 4562", name: "Stone Beige", hex: "#D8C6A5" },
            { code: "MIST 4500", name: "Latte Mist", hex: "#D7C2A6" },
            { code: "MIST 4078", name: "Moonlit Grey", hex: "#B6B5AF" },
            { code: "MIST 4901", name: "Shadow Mist", hex: "#8C8B85" },
            { code: "MIST 4681", name: "Honey Bronze", hex: "#B48A4F" },
            { code: "MIST 4419", name: "Steel Tide", hex: "#7C8790" },
            { code: "MIST 4789", name: "Breez Blue", hex: "#A8C1D1" },
            { code: "MIST 4530", name: "Crystal White", hex: "#F6F2E9" },
            { code: "MIST 4628", name: "Morning Breeze", hex: "#DDE4DA" },
            { code: "MIST 4142", name: "Cool Horizon", hex: "#B9C7CE" },
            { code: "MIST 4509", name: "Palm Oasis", hex: "#7EA279" },
            { code: "MIST 4913", name: "Golden Lime", hex: "#D6C56A" },
            { code: "MIST 4517", name: "Desert Dune", hex: "#D3B688" },
            { code: "MIST 4066", name: "Earthen Glow", hex: "#B67B4E" },
            { code: "MIST 4984", name: "Olive Bloom", hex: "#8E9057" },
            { code: "MIST 4852", name: "Silk Rose", hex: "#E7C5C0" },
            { code: "MIST 4197", name: "Oat White", hex: "#EDE3CE" },
            { code: "MIST 4235", name: "Violet Bloom", hex: "#B29BC0" },
            { code: "MIST 4451", name: "Desert Biscuit", hex: "#D7B98F" },
            { code: "MIST 4060", name: "Storm Cloud", hex: "#8F9297" },
            { code: "MIST 4354", name: "Clear White", hex: "#F7F3EA" },
        ]),
    },
};

export const COLLECTION_KEYS = Object.keys(COLLECTIONS);
